import { useState, useRef, useCallback } from 'react';
import { getAWSCredentials } from '../auth/auth';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Streams from AgentCore InvokeAgentRuntime via the JS SDK.
 * Parses SSE `data:` lines from the response body.
 */
export const useAgentChat = (agentRuntimeArn: string) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionIdRef = useRef(crypto.randomUUID() + '-' + Date.now());
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const sendMessage = useCallback(async (prompt: string) => {
    setError(null);
    const assistantIdx = messagesRef.current.length + 1;

    setMessages((prev) => prev.concat(
      { role: 'user', content: prompt },
      { role: 'assistant', content: '' },
    ));
    setStreaming(true);

    try {
      const sdk = await import('@aws-sdk/client-bedrock-agentcore');
      const credentials = await getAWSCredentials();
      const client = new sdk.BedrockAgentCoreClient({ region: 'us-east-1', credentials });

      const response = await client.send(
        new sdk.InvokeAgentRuntimeCommand({
          agentRuntimeArn,
          runtimeSessionId: sessionIdRef.current,
          payload: new TextEncoder().encode(JSON.stringify({
            prompt,
            session_id: sessionIdRef.current,
          })),
        }),
      );

      const body = response.response as unknown as ReadableStream<Uint8Array>;
      if (!body) throw new Error('No response body');

      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processChunk = (result: ReadableStreamReadResult<Uint8Array>): Promise<void> => {
        if (result.done) return Promise.resolve();

        buffer += decoder.decode(result.value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (let i = 0; i < lines.length; i++) {
          const trimmed = lines[i].trim();
          if (!trimmed || trimmed.indexOf('data:') !== 0) continue;
          const data = trimmed.slice(5).trim();
          if (!data || data === '[DONE]') continue;

          let text = data;
          try {
            const parsed = JSON.parse(data);
            if (typeof parsed === 'string') text = parsed;
            else if (parsed.data) text = parsed.data;
            else continue;
          } catch {
            // Not JSON — use raw SSE data as plain text (expected for non-structured responses)
          }

          const captured = text;
          setMessages((prev) => {
            const updated = prev.slice();
            updated[assistantIdx] = {
              role: 'assistant',
              content: updated[assistantIdx].content + captured,
            };
            return updated;
          });
        }

        return reader.read().then(processChunk);
      };

      await reader.read().then(processChunk);
    } catch (err: any) {
      const msg = err.message || 'Failed to get response from agent';
      setError(msg);
      setMessages((prev) => prev.filter((_, i) => i !== assistantIdx));
    } finally {
      setStreaming(false);
    }
  }, [agentRuntimeArn]);

  const reset = useCallback(() => {
    setMessages([]);
    setError(null);
    setStreaming(false);
    sessionIdRef.current = crypto.randomUUID() + '-' + Date.now();
  }, []);

  return { messages, streaming, error, sendMessage, reset };
};
